import { VENDOR_ONBOARD_REST_URLS } from "../../common-utilities/utils/end-points/vendor-onboard";
import { FINANCE_REST_URLS } from "../../common-utilities/utils/end-points/finance-v2";
import { HTTP_METHODS, invokeApi } from "./http-service";
import { clearOfflineData, setOfflineData } from "./offline-services";
import { INTRANETHOSTNAME } from "../../common-utilities/utils/end-points";
import { getOfflineData } from "./offline-services";

// Departments are centrally managed (subway-intranet-api) and only exposed by numeric id --
// there is no name-based lookup, so this must match this exact department's name (with the
// "&", not "and") to be resolved to its id below.
const PROCUREMENT_DEPARTMENT_NAME = "Procurement & Supply Chain";
const INDEPENDENT_PO_PROCUREMENT_BUDGET_EXTRA_DEPARTMENT_NAMES = ["Maintenance", "M&R"];
const normalizeDepartmentName = (name) =>
  typeof name === "string" ? name.replace(/\s+/g, "").toLowerCase() : "";
const normalizedIndependentPoProcurementBudgetExtraDepartmentNames = new Set(
  INDEPENDENT_PO_PROCUREMENT_BUDGET_EXTRA_DEPARTMENT_NAMES.map(normalizeDepartmentName)
);
export const redirectToIntranetPortal = () => {
  clearOfflineData("user");
  clearOfflineData("tokens");
  setTimeout(() => {
    window.location.href = "/";
  }, 200);
};

export const getUserDetailsFromIntranet = (
  Authorization,
  customLogic,
  tokens,
  params
) => {
  invokeApi(
    HTTP_METHODS.POST,
    `${INTRANETHOSTNAME}${VENDOR_ONBOARD_REST_URLS.INTRANET_LOGIN}`,
    null,
    params,
    Authorization
  )
    .then((res) => {
      if (res?.message) {
        redirectToIntranetPortal();
        return;
      }
      if (tokens) {
        setOfflineData("tokens", tokens);
      }
      const role = res?.role?.toUpperCase();
      const userDepartments = (res?.userDepartments || []).map(Number);

      // const finishLogin = (isProcurement) => {
      //   setOfflineData("user", { ...res, role, isProcurementUser: isProcurement });
      const finishLogin = (isProcurement, isIndependentPoProcurementBudgetDepartment = false) => {
        setOfflineData("user", {
          ...res,
          role,
          isProcurementUser: isProcurement,
          isIndependentPoProcurementBudgetDepartment,
        });
        if (typeof customLogic === "function") {
          customLogic(res);
        }
      };

      // Resolve Procurement & Supply Chain membership once at login, rather than on every
      // gated screen -- mirrors how `role` above is normalized once and read synchronously
      // everywhere else (isStoreRole() etc.).
      invokeApi(
        HTTP_METHODS.GET,
        `${INTRANETHOSTNAME}${FINANCE_REST_URLS.DEPARTMENTS}`,
        null,
        { page: 1, limit: 1000 }
      )
        .then((departments) => {
          const procurementDept = Array.isArray(departments)
            ? departments.find((d) => d.name === PROCUREMENT_DEPARTMENT_NAME)
            : null;
          // finishLogin(!!procurementDept && userDepartments.includes(Number(procurementDept._id)));
          const userDepartmentIdsWithPrimary =
            res?.department != null ? [...userDepartments, Number(res.department)] : userDepartments;
          const independentPoProcurementBudgetDepartmentIds = Array.isArray(departments)
            ? departments
                .filter((d) =>
                  normalizedIndependentPoProcurementBudgetExtraDepartmentNames.has(
                    normalizeDepartmentName(d.name)
                  )
                )
                .map((d) => Number(d._id))
            : [];
          finishLogin(
            !!procurementDept && userDepartments.includes(Number(procurementDept._id)),
            independentPoProcurementBudgetDepartmentIds.some((id) =>
              userDepartmentIdsWithPrimary.includes(id)
            )
          );
        })
        .catch(() => {
          // Department lookup failing shouldn't block login -- fall back to "not Procurement",
          // the safe default (hides the PR-linked PO flow rather than wrongly granting it; the
          // backend enforces the same restriction independently regardless).
          finishLogin(false);
        });
    })
    .catch(() => {
      redirectToIntranetPortal();
    });
};

export const getFileNameType = (url) => {
  if (url) {
    const urlSplits = url.split("/");
    if (urlSplits.length > 0) {
      const lastSplits = urlSplits[urlSplits.length - 1].split(".");
      return lastSplits[lastSplits.length - 1];
    }
  }

  return "";
};

export const getFileName = (url) => {
  if (url) {
    const urlSplits = url.split("/");
    if (urlSplits.length > 0) {
      return urlSplits[urlSplits.length - 1];
    }
  }

  return "";
};

export const isStoreRole = () => {
  const userDetails = getOfflineData("user") || {};
  return (userDetails.role || "").trim().toUpperCase() === "STORE";
};

// PR->PO conversion (and PO creation from a PR) is owned by Procurement & Supply Chain for
// any PR -- see isProcurementUser in the backend's store-role.service.js for the mirrored
// server-side enforcement of the same rule. Reads the flag computed once at login by
// getUserDetailsFromIntranet above.
export const isProcurementUser = () => {
  const userDetails = getOfflineData("user") || {};
  return userDetails.isProcurementUser === true;
};

export const canUseProcurementBudgetForIndependentPO = () => {
  const userDetails = getOfflineData("user") || {};
  return isProcurementUser() || userDetails.isIndependentPoProcurementBudgetDepartment === true;
};

// The base "user" role -- a regular department account (P&SC accounts carry this same role
// string; they're distinguished only by department membership, see isProcurementUser above).
// Mirrors store-role.service.js#isBaseUserRole on the backend.
export const isBaseUserRole = () => {
  const userDetails = getOfflineData("user") || {};
  return (userDetails.role || "").trim().toUpperCase() === "USER";
};

export const isDepartmentUserRole = () => {
  const userDetails = getOfflineData("user") || {};
  const role = (userDetails.role || "").trim().toUpperCase();
  return role === "DEPARTMENT USER" || role === "DEPARTMENT_USER";
};

// "PR PO RC" -- regular-user access plus PR creation, PR-linked + independent POs, and
// DEPARTMENT USER's Masters access (Items/Vendors/Vendor Item Mapping). Mirrors
// role-enitity-visibility.service.js#isPrPoRcRole on the backend.
export const isPrPoRcRole = () => {
  const userDetails = getOfflineData("user") || {};
  const role = (userDetails.role || "").trim().toUpperCase();
  return role === "PR PO RC USER" || role === "PR_PO_RC_USER";
};

// Roles that get DEPARTMENT USER's Masters access (tab + Vendor Item Mapping create/edit).
export const hasDepartmentUserMasterAccess = () => isDepartmentUserRole() || isPrPoRcRole();

const getUserDepartmentIds = () => {
  const userDetails = getOfflineData("user") || {};
  const ids = new Set((userDetails.userDepartments || []).map(Number));
  if (userDetails.department != null) ids.add(Number(userDetails.department));
  return ids;
};

const DEPARTMENT_LOCKED_PR_ROLES = [
  "DEPARTMENT USER",
  "DEPARTMENT_USER",
  "DEPARTMENT READ ONLY",
  "DEPARTMENT_READ_ONLY",
];

const isHodPrRole = () => {
  const userDetails = getOfflineData("user") || {};
  const role = (userDetails.role || "").trim().toUpperCase();
  const parts = role.split("&").map((part) => part.trim());
  return role === "HOD" || (parts.includes("HOD") && parts.includes("USER"));
};

export const canViewPRs = () => isViewAllowed("pr") || isStoreRole();

export const getPrDepartmentScope = () => {
  const userDetails = getOfflineData("user") || {};
  const role = (userDetails.role || "").trim().toUpperCase();
  const departmentIds = [...getUserDepartmentIds()].filter((id) => Number.isFinite(id));
  const primaryDepartment = Number(userDetails.department);
  const defaultDepartment = departmentIds.includes(primaryDepartment)
    ? primaryDepartment
    : departmentIds[0] ?? null;
  if (isProcurementUser()) {
    return { restricted: false, locked: false, departmentIds, defaultDepartment };
  }
  const locked = DEPARTMENT_LOCKED_PR_ROLES.includes(role);
  const restricted = locked || isPrPoRcRole() || isHodPrRole();
  return { restricted, locked, departmentIds, defaultDepartment };
};

// PRs raised under either of these two categories can only ever be converted to a PO by
// Procurement & Supply Chain, regardless of which department raised the PR -- mirrors
// store-role.service.js#RESTRICTED_CATEGORY_NAMES on the backend. Category has no
// slug/enum, only this free-text `name`, so this matches by name (same convention as
// filterStoreRoleLineItems in create-pr/helper.js). The backend always populates
// pr.items[].category into a full { id, name, ... } object on fetch, so `.name` is
// available here with no extra call.
export const RESTRICTED_CATEGORY_NAMES = ["Procurement Budget", "Procurement Budget Opex"];
const normalizedRestrictedCategoryNames = new Set(
  RESTRICTED_CATEGORY_NAMES.map((name) => name.toLowerCase())
);

const isRestrictedCategory = (category) => {
  const name = category?.name;
  return typeof name === "string" && normalizedRestrictedCategoryNames.has(name.trim().toLowerCase());
};

// Same name-matching rule as isRestrictedCategory above, for call sites that only have the
// bare name string rather than a populated category object -- independent PO creation's
// budget-line-item dropdown (independent-po-modal.js) has no PR/category doc to read from,
// only each budget LineItem's own `name`, which mirrors its Category's name 1:1 (see
// line-item.model.js's field docs).
export const isRestrictedCategoryName = (name) =>
  typeof name === "string" && normalizedRestrictedCategoryNames.has(name.trim().toLowerCase());

export const prHasRestrictedCategory = (pr) =>
  (pr?.items || []).some((item) => isRestrictedCategory(item?.category));

// Mirrors store-role.service.js#canConvertPRToPO on the backend, so the UI only ever offers
// a Convert-to-PO/Bulk-PO action the server will actually allow: Procurement & Supply Chain
// may convert any PR, any category; a regular department "user" may convert only their own
// department's PRs (never a store-originated PR, which has no department to match against),
// and never a PR carrying a restricted category (Procurement Budget / Procurement Budget
// Opex) even when it is their own department's PR; every other role (Store included) has no
// conversion path at all. `pr` needs at least its `department` and `items[].category` fields
// (department null for a store-originated PR).
// Old rule, kept for a possible revert:
// export const canConvertPRToPO = (pr) => {
//   if (isProcurementUser()) return true;
//   if (!isBaseUserRole()) return false;
//   if (pr?.department == null) return false;
//   if (!getUserDepartmentIds().has(Number(pr.department))) return false;
//   if (prHasRestrictedCategory(pr)) return false;
//   return true;
// };
// The plain "user" role has no PR access now (P&SC on it included); PR PO RC User takes over
// the old "user" own-department rule.
export const canConvertPRToPO = (pr) => {
  if (isBaseUserRole()) return false;
  if (isProcurementUser()) return true;
  if (!isPrPoRcRole()) return false;
  if (pr?.department == null) return false;
  if (!getUserDepartmentIds().has(Number(pr.department))) return false;
  if (prHasRestrictedCategory(pr)) return false;
  return true;
};

export const isCreateAllowed = (type) => {
  const userDetails = getOfflineData("user") || {};
  const { userRights = {} } = userDetails;
  if (userRights[type]) {
    return userRights[type].create;
  }
  return false;
};

export const isUploadAllowed = (type) => {
  const userDetails = getOfflineData("user") || {};
  const { userRights = {} } = userDetails;
  if (userRights[type]) {
    return userRights[type].upload;
  }
  return false;
};

export const isDeleteAllowed = (type) => {
  const userDetails = getOfflineData("user") || {};
  const { userRights = {} } = userDetails;
  if (userRights?.[type]) {
    return userRights[type].delete;
  }
  return false;
};

export const isUpdateAllowed = (type) => {
  const userDetails = getOfflineData("user") || {};
  const { userRights = {} } = userDetails;
  if (userRights?.[type]) {
    return userRights[type].update;
  }
  return false;
};

const VIEWER_ROLE_BY_RIGHT = {
  po: "poViewer",
  budget: "budgetViewer",
};

export const hasViewerRole = (type) => {
  const userDetails = getOfflineData("user") || {};
  return !!userDetails?.viewerRoles?.[VIEWER_ROLE_BY_RIGHT[type]];
};

export const isViewAllowed = (type) => {
  const userDetails = getOfflineData("user") || {};
  const { userRights = {} } = userDetails;
  if (userRights?.[type]?.view) {
    return userRights[type].view;
  }
  return hasViewerRole(type);
};
export const isActionAllowed = (type) => {
  const userDetails = getOfflineData("user") || {};
  const { userRights = {} } = userDetails;
  if (userRights?.[type]) {
    return userRights[type].approveReject;
  }
  return false;
};
